import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, 
  CheckCircle, 
  Award, 
  Shield, 
  Truck,
  Globe,
  Users,
  Clock
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { useSiteContent } from '../context/SiteContentContext';
import { usePageTitle } from '../hooks/usePageTitle';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tetralog truck images - local paths
const TRUCK_IMAGES = {
  hero: '/images/truck-hero.jpg',
  snow: '/images/truck-snow.jpg',
  port: '/images/truck-port.jpg'
};

export default function AboutPage() {
  const { language } = useSiteContent();
  const [timeline, setTimeline] = useState([]);
  const [pageContent, setPageContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Set page title
  usePageTitle('about');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [timelineRes, contentRes] = await Promise.all([
          axios.get(`${API}/timeline`),
          axios.get(`${API}/about-page-content`)
        ]);
        // Sort by order number (ensure numeric sorting)
        const sortedTimeline = timelineRes.data.timeline.sort((a, b) => {
          const orderA = parseInt(a.order) || 0;
          const orderB = parseInt(b.order) || 0;
          return orderA - orderB;
        });
        setTimeline(sortedTimeline);
        if (contentRes.data && Object.keys(contentRes.data).length > 0) {
          setPageContent(contentRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Default translations (fallback)
  const defaultContent = language === 'bg' ? {
    heroSubtitle: 'ЗА TETRALOG',
    heroTitle: 'Над',
    heroTitleHighlight: '20 години',
    heroTitleEnd: 'отлични резултати',
    heroDescription: 'От скромни начала до водеща европейска транспортна компания, нашият път е изграден върху доверие, надеждност и ангажимент към съвършенство.',
    missionSubtitle: 'НАШАТА МИСИЯ',
    missionTitle: 'Доставяме',
    missionTitleHighlight: 'спокойствие',
    missionText1: 'В Tetralog Ltd. нашата мисия е да предоставяме бързи, надеждни и сигурни международни транспортни услуги. Стремим се да отговорим и надхвърлим очакванията на нашите клиенти, като последователно предлагаме висококачествени услуги на разумни цени.',
    missionText2: 'Вярваме, че поверяването на вашите ценности на нас трябва да идва с пълно спокойствие. Нашият опитен екип обработва всяка пратка с най-голямо внимание и професионализъм.',
    values: [
      { icon: Shield, title: 'Надеждност', description: 'Изпълняваме обещанията си винаги. Вашият товар е в сигурни ръце.' },
      { icon: Award, title: 'Качествено обслужване', description: 'Надхвърляме очакванията с професионално боравене и грижа.' },
      { icon: Users, title: 'Фокус върху клиента', description: 'Вашето удовлетворение е наш приоритет. Персонализирани решения за всяка нужда.' },
      { icon: Globe, title: 'Европейско покритие', description: 'Обхващаме всички основни европейски дестинации.' }
    ],
    timelineSubtitle: 'НАШИЯТ ПЪТ',
    timelineTitle: 'История на',
    timelineTitleHighlight: 'компанията',
    credentialsSubtitle: 'АКРЕДИТАЦИИ',
    credentialsTitle: 'Лицензи и',
    credentialsTitleHighlight: 'сертификати',
    credentialsText: 'Поддържаме най-високите индустриални стандарти и притежаваме всички необходими сертификати за международни транспортни операции в Европа.',
    certifications: ['Лицензиран EU превозвач', 'Член на IRU (Международен съюз по пътен транспорт)', 'Пълно застрахователно покритие на товара', 'EURO 6 екологични стандарти', 'Сертификат за достъп до ECO зони'],
    ctaTitle: 'Готови да работите с нас?',
    ctaText: 'Присъединете се към хилядите доволни клиенти, които се доверяват на Tetralog за своите транспортни нужди.',
    ctaButton: 'Свържете се с нас'
  } : {
    heroSubtitle: 'ABOUT TETRALOG',
    heroTitle: 'Over',
    heroTitleHighlight: '20 Years',
    heroTitleEnd: 'of Excellence',
    heroDescription: 'From humble beginnings to becoming a leading European transport company, our journey is built on trust, reliability, and a commitment to excellence.',
    missionSubtitle: 'OUR MISSION',
    missionTitle: 'Delivering',
    missionTitleHighlight: 'Peace of Mind',
    missionText1: 'At Tetralog Ltd., our mission is to provide fast, reliable, and secure international transport services. We strive to meet and exceed the expectations of our customers by consistently offering high-quality service at reasonable prices.',
    missionText2: 'We believe that entrusting your valuables to us should come with complete peace of mind. Our experienced team handles every shipment with the utmost care and professionalism.',
    values: [
      { icon: Shield, title: 'Reliability', description: 'We deliver on our promises, every time. Your cargo is in safe hands.' },
      { icon: Award, title: 'Quality Service', description: 'Exceeding expectations with professional handling and care.' },
      { icon: Users, title: 'Customer Focus', description: 'Your satisfaction is our priority. Personalized solutions for every need.' },
      { icon: Globe, title: 'European Reach', description: 'Comprehensive coverage across all major European destinations.' }
    ],
    timelineSubtitle: 'OUR JOURNEY',
    timelineTitle: 'Company',
    timelineTitleHighlight: 'History',
    credentialsSubtitle: 'CREDENTIALS',
    credentialsTitle: 'Licenses &',
    credentialsTitleHighlight: 'Certifications',
    credentialsText: 'We maintain the highest industry standards and hold all necessary certifications for international transport operations across Europe.',
    certifications: ['Licensed EU Carrier', 'International Road Transportation Union (IRU) Member', 'Full Cargo Insurance Coverage', 'EURO 6 Environmental Standards', 'ECO Zone Access Certification'],
    ctaTitle: 'Ready to Work With Us?',
    ctaText: 'Join thousands of satisfied clients who trust Tetralog for their transport needs.',
    ctaButton: 'Get in Touch'
  };

  // Build content from API or defaults
  const content = pageContent ? {
    heroSubtitle: language === 'bg' ? (pageContent.hero_subtitle_bg || defaultContent.heroSubtitle) : (pageContent.hero_subtitle_en || defaultContent.heroSubtitle),
    heroTitle: language === 'bg' ? (pageContent.hero_title_bg || defaultContent.heroTitle) : (pageContent.hero_title_en || defaultContent.heroTitle),
    heroTitleHighlight: language === 'bg' ? (pageContent.hero_title_highlight_bg || defaultContent.heroTitleHighlight) : (pageContent.hero_title_highlight_en || defaultContent.heroTitleHighlight),
    heroTitleEnd: language === 'bg' ? (pageContent.hero_title_end_bg || defaultContent.heroTitleEnd) : (pageContent.hero_title_end_en || defaultContent.heroTitleEnd),
    heroDescription: language === 'bg' ? (pageContent.hero_description_bg || defaultContent.heroDescription) : (pageContent.hero_description_en || defaultContent.heroDescription),
    missionSubtitle: language === 'bg' ? (pageContent.mission_subtitle_bg || defaultContent.missionSubtitle) : (pageContent.mission_subtitle_en || defaultContent.missionSubtitle),
    missionTitle: language === 'bg' ? (pageContent.mission_title_bg || defaultContent.missionTitle) : (pageContent.mission_title_en || defaultContent.missionTitle),
    missionTitleHighlight: language === 'bg' ? (pageContent.mission_title_highlight_bg || defaultContent.missionTitleHighlight) : (pageContent.mission_title_highlight_en || defaultContent.missionTitleHighlight),
    missionText1: language === 'bg' ? (pageContent.mission_text1_bg || defaultContent.missionText1) : (pageContent.mission_text1_en || defaultContent.missionText1),
    missionText2: language === 'bg' ? (pageContent.mission_text2_bg || defaultContent.missionText2) : (pageContent.mission_text2_en || defaultContent.missionText2),
    values: pageContent.values && pageContent.values.length > 0 ? pageContent.values.map((v, i) => ({
      icon: defaultContent.values[i]?.icon || Shield,
      title: language === 'bg' ? (v.title_bg || defaultContent.values[i]?.title) : (v.title_en || defaultContent.values[i]?.title),
      description: language === 'bg' ? (v.desc_bg || defaultContent.values[i]?.description) : (v.desc_en || defaultContent.values[i]?.description)
    })) : defaultContent.values,
    timelineSubtitle: language === 'bg' ? (pageContent.timeline_subtitle_bg || defaultContent.timelineSubtitle) : (pageContent.timeline_subtitle_en || defaultContent.timelineSubtitle),
    timelineTitle: language === 'bg' ? (pageContent.timeline_title_bg || defaultContent.timelineTitle) : (pageContent.timeline_title_en || defaultContent.timelineTitle),
    timelineTitleHighlight: language === 'bg' ? (pageContent.timeline_title_highlight_bg || defaultContent.timelineTitleHighlight) : (pageContent.timeline_title_highlight_en || defaultContent.timelineTitleHighlight),
    credentialsSubtitle: language === 'bg' ? (pageContent.credentials_subtitle_bg || defaultContent.credentialsSubtitle) : (pageContent.credentials_subtitle_en || defaultContent.credentialsSubtitle),
    credentialsTitle: language === 'bg' ? (pageContent.credentials_title_bg || defaultContent.credentialsTitle) : (pageContent.credentials_title_en || defaultContent.credentialsTitle),
    credentialsTitleHighlight: language === 'bg' ? (pageContent.credentials_title_highlight_bg || defaultContent.credentialsTitleHighlight) : (pageContent.credentials_title_highlight_en || defaultContent.credentialsTitleHighlight),
    credentialsText: language === 'bg' ? (pageContent.credentials_text_bg || defaultContent.credentialsText) : (pageContent.credentials_text_en || defaultContent.credentialsText),
    certifications: language === 'bg' ? (pageContent.certifications_bg || defaultContent.certifications) : (pageContent.certifications_en || defaultContent.certifications),
    ctaTitle: language === 'bg' ? (pageContent.cta_title_bg || defaultContent.ctaTitle) : (pageContent.cta_title_en || defaultContent.ctaTitle),
    ctaText: language === 'bg' ? (pageContent.cta_text_bg || defaultContent.ctaText) : (pageContent.cta_text_en || defaultContent.ctaText),
    ctaButton: language === 'bg' ? (pageContent.cta_button_bg || defaultContent.ctaButton) : (pageContent.cta_button_en || defaultContent.ctaButton)
  } : defaultContent;

  // Show loader while data is loading
  if (isLoading) {
    return (
      <div className="page-loader">
        <div className="page-loader-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="about-page" className="pt-20 bg-slate-50">
      <SEO page="about" />
      {/* Hero Section */}
      <section 
        className="relative py-24 md:py-32"
        style={{ backgroundImage: `url(${TRUCK_IMAGES.snow})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/85 to-white/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <span className="overline mb-4 block">{content.heroSubtitle}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6">
            {content.heroTitle} <span className="text-[#C41E3A]">{content.heroTitleHighlight}</span> {content.heroTitleEnd}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">{content.heroDescription}</p>
        </div>
      </section>

      {/* Mission & Values */}
      <section data-testid="values-section" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="overline mb-4 block">{content.missionSubtitle}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {content.missionTitle} <span className="text-[#C41E3A]">{content.missionTitleHighlight}</span>
              </h2>
              <p className="text-slate-500 mb-6 leading-relaxed">{content.missionText1}</p>
              <p className="text-slate-500 leading-relaxed">{content.missionText2}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {content.values.map((value, index) => (
                <Card key={value.title} className="bg-slate-50 border-slate-100 hover:shadow-lg transition-all rounded-3xl animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                  <CardContent className="p-6">
                    <value.icon className="w-10 h-10 text-[#C41E3A] mb-4" />
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{value.title}</h3>
                    <p className="text-slate-500 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section data-testid="timeline-section" className="section-padding bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="overline mb-4 block">{content.timelineSubtitle}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{content.timelineTitle} <span className="text-[#C41E3A]">{content.timelineTitleHighlight}</span></h2>
          </div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#C41E3A] via-slate-200 to-slate-100 rounded-full" />
            {timeline.map((item, index) => {
              const title = language === 'bg' && item.title_bg ? item.title_bg : item.title_en;
              const description = language === 'bg' && item.description_bg ? item.description_bg : item.description_en;
              return (
                <div key={item.id || item.year} className={`relative flex items-start mb-12 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[#C41E3A] border-4 border-white shadow-lg z-10" />
                  <div className={`ml-12 md:ml-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                    <div className="inline-block px-4 py-1 bg-red-50 rounded-full text-[#C41E3A] text-sm font-bold mb-2">{item.year}</div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
                    <p className="text-slate-500 text-sm">{description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section data-testid="certifications-section" className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="overline mb-4 block">{content.credentialsSubtitle}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{content.credentialsTitle} <span className="text-[#C41E3A]">{content.credentialsTitleHighlight}</span></h2>
              <p className="text-slate-500 mb-8">{content.credentialsText}</p>
              <div className="space-y-4">
                {content.certifications.map((cert, index) => (
                  <div key={index} className="flex items-center gap-3 animate-fade-in-up" style={{ animationDelay: `${index * 0.1}s` }}>
                    <CheckCircle className="w-5 h-5 text-[#C41E3A] flex-shrink-0" />
                    <span className="text-slate-600">{cert}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Award, title: 'IRU', sub: language === 'bg' ? 'Член от 2014' : 'Member Since 2014' },
                { icon: Shield, title: 'EURO 6', sub: language === 'bg' ? 'Флот стандарт' : 'Fleet Standard' },
                { icon: Globe, title: 'EU', sub: language === 'bg' ? 'Лицензиран превозвач' : 'Licensed Carrier' },
                { icon: Truck, title: 'ECO', sub: language === 'bg' ? 'Достъп до зони' : 'Zone Access' }
              ].map((item, i) => (
                <Card key={i} className="bg-slate-50 border-slate-100 p-6 text-center rounded-3xl">
                  <item.icon className="w-12 h-12 text-[#C41E3A] mx-auto mb-4" />
                  <h4 className="text-2xl font-bold text-slate-900 mb-1">{item.title}</h4>
                  <p className="text-slate-500 text-sm">{item.sub}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#C41E3A] to-[#A01830]">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{content.ctaTitle}</h2>
          <p className="text-white/80 mb-8">{content.ctaText}</p>
          <Link to="/contact">
            <button data-testid="about-contact-btn" className="btn-modern btn-light btn-lg">
              {content.ctaButton} <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
}
